import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api } from '@/boot/axios'
import { setDomain, clearDomain, getBaseApiUrl } from '@/boot/axios'

// ─── Types ───────────────────────────────────────────────────────────────────

export interface Enrollment {
  tenant_id: number
  schema_name: string
  role: 'team' | 'customer' | 'both'
  domain: string | null
  business_name: string
  image: string | null
  phone: string | null
  email: string | null
  industry: string | null
  location: Record<string, string> | null
  timezone: string
  currency: string
}

export type EnrollmentMode = 'team' | 'customer'

// ─── Store ───────────────────────────────────────────────────────────────────

export const useEnrollmentStore = defineStore('enrollment', () => {
  // State
  const enrollments = ref<Enrollment[]>([])
  const selectedEnrollment = ref<Enrollment | null>(null)
  const selectedMode = ref<EnrollmentMode | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const isInitialized = ref(false)

  // ─── Getters ─────────────────────────────────────────────────────────────

  /** Enrollments where user is a team member (role = 'team' or 'both') */
  const teamEnrollments = computed(() =>
    enrollments.value.filter(e => e.role === 'team' || e.role === 'both')
  )

  /** Enrollments where user is a customer (role = 'customer' or 'both') */
  const customerEnrollments = computed(() =>
    enrollments.value.filter(e => e.role === 'customer' || e.role === 'both')
  )

  /** True when user needs to pick an enrollment before proceeding */
  const requiresEnrollmentSelection = computed(() => {
    if (enrollments.value.length === 0) return false
    if (!selectedEnrollment.value) return true
    if (selectedEnrollment.value.role === 'both' && !selectedMode.value) return true
    return false
  })

  /** True when user has no enrollments at all (needs to create business or join one) */
  const hasNoEnrollments = computed(() =>
    isInitialized.value && enrollments.value.length === 0
  )

  /** True when an enrollment is selected and domain is set */
  const isEnrollmentReady = computed(() =>
    selectedEnrollment.value !== null && selectedMode.value !== null
  )

  // ─── Actions ─────────────────────────────────────────────────────────────

  /**
   * Fetch enrollments from the backend.
   * Uses the base API URL (not tenant-specific) since this is a user-level endpoint.
   */
  async function fetchEnrollments(): Promise<void> {
    isLoading.value = true
    error.value = null

    try {
      // Call my-enrollments on the base API URL (not tenant domain)
      const baseUrl = getBaseApiUrl()
      const response = await api.get('/api/v1/my-enrollments/', {
        baseURL: baseUrl
      })

      const data = response.data
      enrollments.value = (data.enrollments || []) as Enrollment[]
      isInitialized.value = true

      // Auto-select if only one enrollment
      if (!selectedEnrollment.value && enrollments.value.length === 1) {
        const enrollment = enrollments.value[0]
        // Web dashboard is team-only, so prefer team mode
        const mode: EnrollmentMode = (enrollment.role === 'customer') ? 'customer' : 'team'
        await selectEnrollment(enrollment, mode)
        console.log(`[Enrollment] Auto-selected: ${enrollment.business_name} as ${mode}`)
      } else if (selectedEnrollment.value) {
        // Validate that saved enrollment still exists
        const fresh = enrollments.value.find(
          e => e.tenant_id === selectedEnrollment.value!.tenant_id
        )
        if (fresh) {
          // Update with fresh data
          selectedEnrollment.value = fresh
        } else {
          // Enrollment no longer exists
          console.warn('[Enrollment] Saved enrollment no longer exists, clearing')
          await clearSelection()
        }
      }
    } catch (err: any) {
      console.error('[Enrollment] Failed to fetch enrollments:', err)
      error.value = err.response?.data?.error || err.message || 'Failed to fetch enrollments'
      isInitialized.value = true
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Select an enrollment and set the API domain for subsequent requests.
   */
  async function selectEnrollment(enrollment: Enrollment, mode: EnrollmentMode): Promise<void> {
    // Validate mode is appropriate for enrollment role
    let validatedMode = mode
    if (mode === 'team' && enrollment.role === 'customer') {
      validatedMode = 'customer'
      console.warn('[Enrollment] Mode corrected: team → customer')
    } else if (mode === 'customer' && enrollment.role === 'team') {
      validatedMode = 'team'
      console.warn('[Enrollment] Mode corrected: customer → team')
    }

    selectedEnrollment.value = enrollment
    selectedMode.value = validatedMode

    // Set the domain for API calls
    if (enrollment.domain) {
      setDomain(enrollment.domain)
      console.log(`[Enrollment] Domain set to: ${enrollment.domain}`)
      console.log(`[Enrollment] Selected: ${enrollment.business_name} (${enrollment.role}) as ${validatedMode}`)
    }

    // Persist to localStorage
    _saveToLocalStorage(enrollment, validatedMode)
  }

  /**
   * Clear the current enrollment selection.
   */
  async function clearSelection(): Promise<void> {
    selectedEnrollment.value = null
    selectedMode.value = null
    clearDomain()
    _clearLocalStorage()
  }

  /**
   * Restore enrollment from localStorage for instant startup.
   * Returns true if a valid cached enrollment was restored.
   */
  function restoreFromCache(): boolean {
    try {
      const savedJson = localStorage.getItem('selected_enrollment')
      const savedMode = localStorage.getItem('selected_enrollment_mode') as EnrollmentMode | null
      const savedDomain = localStorage.getItem('domain')

      if (!savedJson || !savedMode || !savedDomain) {
        return false
      }

      const savedEnrollment = JSON.parse(savedJson) as Enrollment
      selectedEnrollment.value = savedEnrollment
      selectedMode.value = savedMode
      enrollments.value = [savedEnrollment] // Placeholder until background refresh

      // Set domain for API calls
      setDomain(savedDomain)

      console.log(`[Enrollment] Restored from cache: ${savedEnrollment.business_name} as ${savedMode}`)
      return true
    } catch (err) {
      console.error('[Enrollment] Failed to restore from cache:', err)
      return false
    }
  }

  /**
   * Reset all enrollment state (on logout).
   */
  function reset(): void {
    enrollments.value = []
    selectedEnrollment.value = null
    selectedMode.value = null
    isLoading.value = false
    error.value = null
    isInitialized.value = false
    clearDomain()
    _clearLocalStorage()
  }

  // ─── Private Helpers ─────────────────────────────────────────────────────

  function _saveToLocalStorage(enrollment: Enrollment, mode: EnrollmentMode): void {
    try {
      localStorage.setItem('selected_enrollment', JSON.stringify(enrollment))
      localStorage.setItem('selected_enrollment_mode', mode)
      if (enrollment.domain) {
        localStorage.setItem('domain', enrollment.domain)
      }
    } catch (err) {
      console.error('[Enrollment] Failed to save to localStorage:', err)
    }
  }

  function _clearLocalStorage(): void {
    localStorage.removeItem('selected_enrollment')
    localStorage.removeItem('selected_enrollment_mode')
    localStorage.removeItem('domain')
  }

  return {
    // State
    enrollments,
    selectedEnrollment,
    selectedMode,
    isLoading,
    error,
    isInitialized,

    // Getters
    teamEnrollments,
    customerEnrollments,
    requiresEnrollmentSelection,
    hasNoEnrollments,
    isEnrollmentReady,

    // Actions
    fetchEnrollments,
    selectEnrollment,
    clearSelection,
    restoreFromCache,
    reset,
  }
})
